import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import { inspectionAiService } from '../../../services/inspectionAiService';
import Icon from '../../../components/AppIcon';

const InspectionAiPanel = ({ selectedUnit, inspectionData, workOrderId, companyId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [recommendations, setRecommendations] = useState('');
  const [parts, setParts] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  // Load existing recommendations when panel opens
  useEffect(() => {
    if (isOpen && workOrderId && !hasLoaded) {
      setHasLoaded(true);
      inspectionAiService?.getByWorkOrder(workOrderId)?.then(existing => {
        if (existing) {
          setSavedId(existing?.id);
          setRecommendations(existing?.recommendations || '');
          setParts(existing?.partsList || []);
        }
      });
    }
  }, [isOpen, workOrderId, hasLoaded]);

  // Parse AI response when it arrives
  useEffect(() => {
    if (!response || isLoading) return;
    try {
      // Try to extract JSON from response
      const jsonMatch = response?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch?.[0]);
        setRecommendations(parsed?.recommendations || response);
        setParts(parsed?.parts || []);
      } else {
        setRecommendations(response);
        setParts([]);
      }
    } catch {
      setRecommendations(response);
      setParts([]);
    }
  }, [response, isLoading]);

  const handleGenerate = () => {
    const prompt = inspectionAiService?.buildPrompt(selectedUnit, inspectionData);
    sendMessage([
      { role: 'system', content: 'Eres un mecánico experto. Responde SOLO en JSON válido sin markdown.' },
      { role: 'user', content: prompt }
    ], { temperature: 0.4, max_tokens: 1500 });
  };

  const handleSave = async () => {
    if (!workOrderId || !companyId) {
      setSaveMsg('Selecciona una unidad primero.');
      return;
    }
    setIsSaving(true);
    setSaveMsg('');
    try {
      const rawInput = { vehicleInfo: selectedUnit, inspectionData };
      if (savedId) {
        await inspectionAiService?.update(savedId, recommendations, parts);
      } else {
        const saved = await inspectionAiService?.save(workOrderId, companyId, recommendations, parts, rawInput);
        setSavedId(saved?.id);
      }
      setSaveMsg('✓ Guardado correctamente');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error al guardar: ' + (err?.message || ''));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePartChange = (idx, field, value) => {
    setParts(prev => prev?.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleAddPart = () => {
    setParts(prev => [...(prev || []), { name: '', qty: 1, note: '' }]);
  };

  const handleRemovePart = (idx) => {
    setParts(prev => prev?.filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
      {/* Header / Toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-smooth rounded-lg"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-primary/10">
            <Icon name="Sparkles" size={16} className="text-primary" />
          </div>
          <span className="text-sm font-semibold text-foreground">IA — Recomendaciones y Piezas</span>
          {(recommendations || parts?.length > 0) && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-success/15 text-success font-medium">Generado</span>
          )}
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          {/* Generate button */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isLoading || !selectedUnit}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-smooth"
            >
              {isLoading
                ? <><Icon name="Loader2" size={15} className="animate-spin" /><span>Generando...</span></>
                : <><Icon name="Sparkles" size={15} /><span>Generar recomendaciones y piezas</span></>}
            </button>
            {!selectedUnit && (
              <span className="text-xs text-muted-foreground">Selecciona una unidad primero</span>
            )}
            {error && (
              <span className="text-xs text-destructive">Error: {error?.message}</span>
            )}
          </div>

          {/* Recommendations textarea */}
          {(recommendations || isLoading) && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Recomendaciones técnicas (editable)</label>
              <textarea
                value={recommendations}
                onChange={(e) => setRecommendations(e?.target?.value)}
                rows={6}
                placeholder="Las recomendaciones aparecerán aquí..."
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              />
            </div>
          )}

          {/* Parts list */}
          {(parts?.length > 0 || recommendations) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground">Lista de refacciones sugeridas (editable)</label>
                <button
                  type="button"
                  onClick={handleAddPart}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                >
                  <Icon name="Plus" size={12} />
                  Agregar
                </button>
              </div>
              <div className="space-y-2">
                {parts?.map((part, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                    <input
                      type="text"
                      value={part?.name || ''}
                      onChange={(e) => handlePartChange(idx, 'name', e?.target?.value)}
                      placeholder="Nombre de pieza"
                      className="col-span-5 px-2 py-1.5 rounded border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <input
                      type="number"
                      value={part?.qty || 1}
                      onChange={(e) => handlePartChange(idx, 'qty', parseInt(e?.target?.value) || 1)}
                      min={1}
                      className="col-span-1 px-2 py-1.5 rounded border border-border bg-background text-sm text-center focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <input
                      type="text"
                      value={part?.note || ''}
                      onChange={(e) => handlePartChange(idx, 'note', e?.target?.value)}
                      placeholder="Nota técnica"
                      className="col-span-5 px-2 py-1.5 rounded border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePart(idx)}
                      className="col-span-1 p-1.5 text-muted-foreground hover:text-destructive transition-smooth"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
                {parts?.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Sin refacciones sugeridas aún.</p>
                )}
              </div>
            </div>
          )}

          {/* Save button */}
          {(recommendations || parts?.length > 0) && (
            <div className="flex items-center gap-3 pt-2 border-t border-border">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 disabled:opacity-50 transition-smooth"
              >
                {isSaving
                  ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Guardando...</span></>
                  : <><Icon name="Save" size={14} /><span>Guardar recomendaciones</span></>}
              </button>
              {saveMsg && (
                <span className={`text-xs ${saveMsg?.startsWith('✓') ? 'text-success' : 'text-destructive'}`}>
                  {saveMsg}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionAiPanel;
